// src/pages/payment/PaymentSuccessPage.jsx
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { getPaymentStatus } from "../../../services/subscription.service";

const POLL_INTERVAL_MS = 1500;
const MAX_ATTEMPTS = 20; // 20 × 1.5s = 30s max wait

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const orderId = searchParams.get("orderId");

  const [phase, setPhase] = useState<"checking" | "paid" | "timeout" | "failed">(
    "checking"
  );
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!orderId) {
      setPhase("failed");
      return;
    }

    let attempts = 0;

    const poll = async () => {
      if (cancelledRef.current) return;
      attempts += 1;

      try {
        const res = await getPaymentStatus(orderId);

        if (!res?.success) {
          setPhase("failed");
          return;
        }

        if (res.data.status === "PAID") {
          setPhase("paid");

          const ctxRaw = sessionStorage.getItem("payzone_signup_ctx");
          const ctx = ctxRaw ? JSON.parse(ctxRaw) : {};
          sessionStorage.removeItem("payzone_signup_ctx");

          setTimeout(() => {
            navigate("/auth/account-created", {
              state: {
                customerType: ctx.customerType,
                customerId: ctx.customerId,
                email: ctx.email,
                subscription: { id: res.data.subscriptionId },
              },
            });
          }, 1000);
          return;
        }

        if (res.data.status === "FAILED" || res.data.status === "CANCELLED") {
          setPhase("failed");
          return;
        }

        // Still PENDING
        if (attempts >= MAX_ATTEMPTS) {
          setPhase("timeout");
          return;
        }

        setTimeout(poll, POLL_INTERVAL_MS);
      } catch {
        if (attempts >= MAX_ATTEMPTS) {
          setPhase("failed");
        } else {
          setTimeout(poll, POLL_INTERVAL_MS);
        }
      }
    };

    poll();

    return () => {
      cancelledRef.current = true;
    };
  }, [orderId, navigate]);

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-white rounded-2xl shadow-xl border border-[#E5E7EB] p-8 text-center">
        {phase === "checking" && (
          <>
            <Loader2 className="w-12 h-12 animate-spin text-[#1E6DFF] mx-auto mb-4" />
            <h2 className="text-xl text-[#2D323A] mb-2">Confirming payment…</h2>
            <p className="text-sm text-[#717182]">
              This usually takes just a few seconds.
            </p>
          </>
        )}

        {phase === "paid" && (
          <>
            <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-4" />
            <h2 className="text-xl text-[#2D323A] mb-2">Payment successful</h2>
            <p className="text-sm text-[#717182]">Redirecting…</p>
          </>
        )}

        {phase === "timeout" && (
          <>
            <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl text-[#2D323A] mb-2">Still processing</h2>
            <p className="text-sm text-[#717182] mb-4">
              Your payment is being verified. This can take up to a minute.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-[#1E6DFF] text-white rounded-xl"
            >
              Check again
            </button>
          </>
        )}

        {phase === "failed" && (
          <>
            <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
            <h2 className="text-xl text-[#2D323A] mb-2">Payment not completed</h2>
            <p className="text-sm text-[#717182] mb-4">
              We couldn't confirm your payment. You have not been charged, or if
              you were, please contact support with your order ID.
            </p>
            <button
              onClick={() => navigate("/auth/subscription")}
              className="px-4 py-2 bg-[#1E6DFF] text-white rounded-xl"
            >
              Try again
            </button>
          </>
        )}
      </div>
    </div>
  );
}